import { ThgThgViewModelGen } from "@/services/thg/v1/data-contracts";
import { ThgVehicleTypes } from "@/store/enum/thg/thg-vehicle-types.enum";
import { CountryCodeEnum } from "../enum/country-code.enum";
import { UnitEnum } from "../enum/unit.enum";
import { $t } from "../utility/t";
import { getKwhForVehicleClass, VEHICLE_CLASS_CONFIG } from "./vehicle-class.config";

/**
 * Converts a unit-based price (per kWh, MWh or piece) into the effective price for one
 * vehicle of the given class, based on the country's kWh allowance per vehicle class.
 * Returns undefined when no allowance is configured for the country and vehicle class.
 *
 * @param pricePerUnit the configured price per unit
 * @param unit the unit the price is configured in
 * @param vehicleType the vehicle class to compute the price for
 * @param countryCode the country whose allowance applies
 * @returns the price per vehicle rounded to 2 decimals, or undefined
 */
export function getPricePerVehicleClass(
  pricePerUnit: number,
  unit: UnitEnum,
  vehicleType: ThgVehicleTypes,
  countryCode: CountryCodeEnum
): number | undefined {
  if (unit === UnitEnum.PIECE) {
    return Math.round(pricePerUnit * 100) / 100;
  }

  const amountInKwh = getKwhForVehicleClass(vehicleType, countryCode);
  if (amountInKwh === undefined) {
    return undefined;
  }

  const result = unit === UnitEnum.KWH ? pricePerUnit * amountInKwh : pricePerUnit * (amountInKwh / 1000);

  return Math.round(result * 100) / 100;
}

/**
 * The fields of a THG view model that are read for price calculations.
 * Both `ThgThgViewModelGen` and `ThgThgMeViewModelGen` are assignable to this subset.
 */
type ThgPriceRelevantFields = Pick<
  ThgThgViewModelGen,
  "payoutConfiguration" | "registration" | "countryCode" | "promotionConfiguration"
>;

export class ThgPriceHelper {
  constructor(private thg: ThgPriceRelevantFields) {}

  private _vehicleClassMwhMap = VEHICLE_CLASS_CONFIG;

  /**
   * Berechnet den Preis basierend auf Fahrzeugklasse, Land und Payout-Konfiguration.
   * Liefert 0 zurück, falls keine passenden Daten gefunden werden oder eine unbekannte Einheit vorliegt.
   */
  get price(): number {
    const { unit, revenue } = this.thg.payoutConfiguration;
    const vehicleClass = this.thg.registration.vehicleClass;
    const countryCode = this.thg.countryCode;

    // Finde den passenden Eintrag in der Map basierend auf Fahrzeugklasse und Land
    const vehicleData = this._vehicleClassMwhMap.find(
      (v) => v.vehicleType === vehicleClass && v.countryCode === countryCode
    );

    if (!vehicleData) {
      return 0;
    }

    const amountInKwh = vehicleData.amountInKwh;

    switch (unit) {
      case UnitEnum.KWH:
        return revenue * amountInKwh;
      case UnitEnum.MWH:
        return revenue * (amountInKwh / 1000);
      case UnitEnum.PIECE:
        return revenue;
      default:
        return 0;
    }
  }

  /**
   * The payout price normalized to EUR per MWh.
   * Returns undefined for prices configured per piece, as no MWh price exists there.
   */
  get pricePerMwh(): number | undefined {
    const { unit, revenue } = this.thg.payoutConfiguration;

    switch (unit) {
      case UnitEnum.KWH:
        return revenue * 1000;
      case UnitEnum.MWH:
        return revenue;
      default:
        return undefined;
    }
  }

  /**
   * The configured promotion bonus, 0 if no promotion is configured.
   */
  get promotionValue(): number {
    return this.thg.promotionConfiguration?.value || 0;
  }

  /**
   * The vehicle price including the promotion bonus.
   * Returns 0 when the base price is not computable, so that promotion-only
   * amounts never leak into sums or exports for vehicles without a price.
   */
  get totalPrice(): number {
    if (!this.price) {
      return 0;
    }

    return this.price + this.promotionValue;
  }

  /**
   * The composition of the total price as a formatted string, e.g. "220,00 € + 30,00 € Bonus".
   * Empty when no promotion is configured or the price is not computable.
   */
  get priceCompositionAsString(): string {
    if (!this.promotionValue || !this.price) {
      return "";
    }

    return $t("components.thg.ThgPriceHelper.composition", {
      price: this.formatCurrency(this.price),
      bonus: this.formatCurrency(this.promotionValue)
    });
  }

  /**
   * The price per MWh as a formatted string.
   * Returns a dash when no MWh price exists (per-piece prices) or no price is configured yet.
   */
  get pricePerMwhAsString(): string {
    if (!this.pricePerMwh) {
      return "–";
    }

    return this.formatCurrency(this.pricePerMwh);
  }

  /**
   * Formats a value as EUR currency, consistent with the existing price formatting.
   */
  private formatCurrency(value: number): string {
    return value.toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR"
    });
  }

  /**
   * Gibt den berechneten Preis als formatierten String zurück.
   * - Ist die Fahrzeugklasse nicht vorhanden, leer oder nicht in der Map vorhanden, wird
   *   "fahrzeugklasse in Prüfung" zurückgegeben.
   * - Ist der Preis 0, wird "Preis steht noch nicht fest" zurückgegeben.
   */
  get priceAndPromotionAsString(): string {
    const vehicleClass = this.thg.registration.vehicleClass;
    if (!vehicleClass || vehicleClass === "") {
      return $t("components.thg.ThgPriceHelper.vehicleClassPending");
    }
    // Überprüfe, ob ein passender Eintrag in der Map vorhanden ist
    const matchingVehicleData = this._vehicleClassMwhMap.find(
      (v) => v.vehicleType === vehicleClass && v.countryCode === this.thg.countryCode
    );
    if (!matchingVehicleData) {
      return $t("components.thg.ThgPriceHelper.vehicleClassPending");
    }

    if (this.price === 0) {
      return $t("components.thg.ThgPriceHelper.priceNotSet");
    }

    const computedPrice = this.price + (this.thg?.promotionConfiguration?.value || 0);
    return computedPrice.toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR"
    });
  }
}
